import { useToasts } from '@vestaboard/installables'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { GraphQLError } from 'graphql'
import { ApolloError } from 'apollo-boost'

const NOT_FOUND_ERROR_TYPE = 'NotFound'

export type V2GraphqlError = {
  __typename: string
  type: string
  error: string | null
}

type NonV2GraphqlError<T> = Exclude<T, V2GraphqlError>

export const useV2GraphqlMutationErrorHandler = () => {
  const { addToast } = useToasts()

  return useCallback(
    <T extends { __typename?: string }>(
      context: string,
      data?: T | V2GraphqlError,
      errors?: GraphQLError[]
    ): { success: false; data: null } | { success: true; data: NonV2GraphqlError<T> } => {
      if (errors) {
        console.error(context, errors)
        addToast(`Error ${context}`, {
          appearance: 'error',
          autoDismiss: true
        })
        return {
          success: false,
          data: null
        }
      }

      if (!data) {
        console.log(context, 'No data returned despite there being no GraphQL errors')
        addToast(`Error ${context}`, {
          appearance: 'error',
          autoDismiss: true
        })
        return {
          success: false,
          data: null
        }
      }

      if ('type' in data && 'error' in data) {
        console.error(context, data)
        addToast(`Error ${context}: ${data.type} - ${data.error}`, {
          appearance: 'error',
          autoDismiss: true
        })
        return {
          success: false,
          data: null
        }
      }

      return {
        success: true,
        data: data as NonV2GraphqlError<T>
      }
    },
    [addToast]
  )
}

export const useV2GraphqlQueryErrorHandler = <T extends { __typename?: string }>(
  context: string,
  loading: boolean,
  data?: T | V2GraphqlError,
  error?: ApolloError,
  options?: {
    notifyOnNotFound?: boolean
  }
) => {
  const { addToast } = useToasts()
  const notifyOnNotFound = options?.notifyOnNotFound ?? true
  const lastErrorKeyRef = useRef<string | null>(null)

  const errorKey = useMemo(() => {
    if (loading) {
      return null
    }

    if (error) {
      return `${context}:apollo:${error.message}`
    }

    if (!data) {
      return `${context}:no-data`
    }

    if ('type' in data && 'error' in data) {
      if (data.type === NOT_FOUND_ERROR_TYPE && !notifyOnNotFound) {
        return null
      }
      return `${context}:v2:${data.type}:${data.error ?? ''}`
    }

    return null
  }, [context, data, error, loading, notifyOnNotFound])

  useEffect(() => {
    if (loading) {
      return
    }
    if (!errorKey || lastErrorKeyRef.current === errorKey) {
      return
    }
    lastErrorKeyRef.current = errorKey

    if (error) {
      console.error(context, error)
      addToast(`Error ${context}`, {
        appearance: 'error',
        autoDismiss: true
      })
      return
    }

    if (!data) {
      console.log(context, 'No data returned despite there being no GraphQL errors')
      addToast(`Error ${context}`, {
        appearance: 'error',
        autoDismiss: true
      })
      return
    }
    if ('type' in data && 'error' in data) {
      if (data.type === NOT_FOUND_ERROR_TYPE && !notifyOnNotFound) {
        return
      }
      console.error(context, data)
      addToast(`Error ${context}: ${data.type} - ${data.error}`, {
        appearance: 'error',
        autoDismiss: true
      })
      return
    }
  }, [context, data, error, loading, addToast, notifyOnNotFound, errorKey])

  if (loading) {
    return {
      notFound: false,
      success: false,
      loading: true,
      data: null
    } as const
  }

  if (error) {
    return {
      notFound: false,
      success: false,
      loading: false,
      data: null
    } as const
  }

  if (!data) {
    return {
      notFound: false,
      success: false,
      loading: false,
      data: null
    } as const
  }

  if ('type' in data && 'error' in data) {
    if (data.type === NOT_FOUND_ERROR_TYPE) {
      return {
        notFound: true,
        success: false,
        loading: false,
        data: null
      } as const
    }
    return {
      notFound: false,
      success: false,
      loading: false,
      data: null
    } as const
  }

  return {
    notFound: false,
    success: true,
    loading: false,
    data: data as NonV2GraphqlError<T>
  } as const
}
